import { useEffect, useState } from 'react'
import { ChevronDown, X } from 'lucide-react'

import { Button } from 'components/ui/button'
import { Checkbox } from 'components/ui/checkbox'
import { Input } from 'components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover'
import { Spinner } from 'components/ui/spinner'
import { cn } from 'lib/utils'

import { useExplorerScope } from '../hooks/useExplorerScope'
import * as explorerService from '../services/explorerService'
import type { ExplorerColumn, ExplorerFilter, ExplorerGrainKey } from '../types/explorer.types'

interface FilterChipProps {
	column: ExplorerColumn
	filter: ExplorerFilter
	grain: ExplorerGrainKey
	onChange: (filter: ExplorerFilter) => void
	onRemove: () => void
	/** Open its editor immediately, for a filter the user has just added. */
	autoOpen?: boolean
}

/**
 * One active filter, as a chip with an inline editor (MYD-368).
 *
 * The control is chosen by the column's `filterKind`, which the SERVER decides.
 * Inferring it from the column `type` instead would give a reference column a
 * text box and it would silently never match, because the stored value is an id
 * and the user would be typing a label.
 *
 * A chip is the only place some filters are visible at all: a filter may target
 * a column that is not displayed, and hiding a column deliberately keeps its
 * filter. So "remove" here is load-bearing rather than a convenience.
 */
export function FilterChip({
	column,
	filter,
	grain,
	onChange,
	onRemove,
	autoOpen = false,
}: FilterChipProps) {
	const [open, setOpen] = useState(autoOpen)

	return (
		<span className="inline-flex items-center rounded-full border border-gray-200 bg-white text-xs">
			<Popover open={open} onOpenChange={setOpen}>
				<PopoverTrigger asChild>
					<button
						type="button"
						className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-l-full hover:bg-gray-50"
					>
						<span className="font-medium text-gray-700">{column.label}</span>
						<span className={cn('truncate max-w-[160px]', summary(filter) ? 'text-primary' : 'text-gray-400')}>
							{summary(filter) || 'any'}
						</span>
						<ChevronDown className="w-3 h-3 text-gray-400" />
					</button>
				</PopoverTrigger>

				<PopoverContent align="start" className="w-64 p-3">
					<FilterEditor column={column} filter={filter} grain={grain} onChange={onChange} />
				</PopoverContent>
			</Popover>

			<button
				type="button"
				onClick={onRemove}
				aria-label={`Remove ${column.label} filter`}
				className="pr-2 pl-1 py-1 text-gray-400 hover:text-destructive rounded-r-full"
			>
				<X className="w-3 h-3" />
			</button>
		</span>
	)
}

/**
 * A date bound as a person would say it. Starter templates store moving dates
 * as tokens the server resolves on every run (MYD-599): `today`, `today+14`,
 * `today-1`. Anything else is a plain date and is shown as it is.
 */
export function formatDateBound(bound: string): string {
	const match = /^today(?:([+-])(\d+))?$/i.exec(bound)
	if (!match) return bound
	if (!match[1]) return 'today'
	const days = Number(match[2])
	const unit = days === 1 ? 'day' : 'days'
	return match[1] === '+' ? `today + ${days} ${unit}` : `${days} ${unit} ago`
}

/** A short, readable description of what a filter is currently doing. */
export function summary(filter: ExplorerFilter): string {
	if (filter.values?.length) {
		return filter.values.length === 1 ? filter.values[0] : `${filter.values.length} selected`
	}
	if (filter.from || filter.to) {
		return [filter.from, filter.to]
			.filter((bound): bound is string => Boolean(bound))
			.map(formatDateBound)
			.join(' to ')
	}
	if (filter.min !== undefined || filter.max !== undefined) {
		return [filter.min, filter.max].filter(v => v !== undefined && v !== '').join(' to ')
	}
	if (typeof filter.value === 'boolean') return filter.value ? 'Yes' : 'No'
	return ''
}

function FilterEditor({
	column,
	filter,
	grain,
	onChange,
}: {
	column: ExplorerColumn
	filter: ExplorerFilter
	grain: ExplorerGrainKey
	onChange: (filter: ExplorerFilter) => void
}) {
	switch (column.filterKind) {
		case 'enum_in':
		case 'id_in':
			return <FacetPicker column={column} filter={filter} grain={grain} onChange={onChange} />

		case 'number_range':
			return (
				<div className="space-y-2">
					<Label>Between</Label>
					<div className="flex items-center gap-2">
						<Input
							type="number"
							value={filter.min ?? ''}
							placeholder="Min"
							onChange={e => onChange({ ...filter, min: blankToUndefined(e.target.value) })}
						/>
						<Input
							type="number"
							value={filter.max ?? ''}
							placeholder="Max"
							onChange={e => onChange({ ...filter, max: blankToUndefined(e.target.value) })}
						/>
					</div>
				</div>
			)

		case 'date_range':
			return (
				<div className="space-y-2">
					<Label>Between</Label>
					<Input
						type="date"
						value={filter.from ?? ''}
						onChange={e => onChange({ ...filter, from: blankToUndefined(e.target.value) })}
					/>
					<Input
						type="date"
						value={filter.to ?? ''}
						onChange={e => onChange({ ...filter, to: blankToUndefined(e.target.value) })}
					/>
				</div>
			)

		case 'bool':
			return (
				<div className="space-y-1">
					{[
						{ label: 'Yes', value: true },
						{ label: 'No', value: false },
					].map(option => (
						<button
							key={option.label}
							type="button"
							onClick={() =>
								onChange({
									...filter,
									// Clicking the active choice clears it, so a boolean filter
									// can be widened again without removing the chip.
									value: filter.value === option.value ? undefined : option.value,
								})
							}
							className={cn(
								'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm',
								filter.value === option.value ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50',
							)}
						>
							<Checkbox checked={filter.value === option.value} />
							{option.label}
						</button>
					))}
				</div>
			)

		case 'text':
		default:
			return (
				<div className="space-y-2">
					<Label>Contains</Label>
					<Input
						autoFocus
						value={filter.values?.[0] ?? ''}
						placeholder={`Search ${column.label.toLowerCase()}`}
						onChange={e => {
							const text = e.target.value
							// An empty value must send NO filter rather than an empty one:
							// the engine treats an absent bound as "no filter" and a
							// present-but-invalid one as a 400.
							onChange({ ...filter, values: text ? [text] : [] })
						}}
					/>
				</div>
			)
	}
}

/**
 * A checkbox list of the values the column actually holds.
 *
 * Loaded from the server rather than typed, so a user filtering by status does
 * not have to remember it is stored as "S1 - Open" rather than "Open".
 */
function FacetPicker({
	column,
	filter,
	grain,
	onChange,
}: {
	column: ExplorerColumn
	filter: ExplorerFilter
	grain: ExplorerGrainKey
	onChange: (filter: ExplorerFilter) => void
}) {
	const [values, setValues] = useState<string[] | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [search, setSearch] = useState('')
	// A scoped report offers only the values its own rows hold (MYD-598).
	const scope = useExplorerScope()
	const scopeType = scope?.type
	const scopeId = scope?.id

	useEffect(() => {
		let cancelled = false
		explorerService
			.getFacetValues(grain, column.key, scopeType && scopeId ? { type: scopeType, id: scopeId } : null)
			.then(result => {
				if (!cancelled) setValues(result)
			})
			.catch(() => {
				// A facet list is an affordance, not the filter itself. Failing to
				// load it must not block filtering, so fall back to typing.
				if (!cancelled) setError('Could not load values')
			})
		return () => {
			cancelled = true
		}
	}, [grain, column.key, scopeType, scopeId])

	const selected = new Set(filter.values ?? [])

	const toggle = (value: string) => {
		const next = new Set(selected)
		if (next.has(value)) next.delete(value)
		else next.add(value)
		onChange({ ...filter, values: [...next] })
	}

	if (error) {
		return (
			<div className="space-y-2">
				<Label>Contains</Label>
				<Input
					value={filter.values?.[0] ?? ''}
					onChange={e => onChange({ ...filter, values: e.target.value ? [e.target.value] : [] })}
				/>
				<p className="text-xs text-gray-400">{error}; type a value instead.</p>
			</div>
		)
	}

	if (!values) return <Spinner type="circular" width="24" containerHeight="80px" />

	const visible = search
		? values.filter(v => v.toLowerCase().includes(search.trim().toLowerCase()))
		: values

	return (
		<div className="space-y-2">
			{values.length > 8 && (
				<Input
					value={search}
					placeholder="Search values"
					onChange={e => setSearch(e.target.value)}
					aria-label={`Search ${column.label} values`}
				/>
			)}

			<div className="max-h-56 overflow-y-auto space-y-0.5">
				{visible.length === 0 ? (
					<p className="text-xs text-gray-400 py-2">No values to choose from.</p>
				) : (
					visible.map(value => (
						<button
							key={value}
							type="button"
							onClick={() => toggle(value)}
							className={cn(
								'w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-sm',
								selected.has(value) ? 'bg-primary/10 text-primary' : 'hover:bg-gray-50',
							)}
						>
							<Checkbox checked={selected.has(value)} />
							<span className="truncate">{value}</span>
						</button>
					))
				)}
			</div>

			{selected.size > 0 && (
				<Button
					variant="text"
					size="input"
					className="w-full"
					onClick={() => onChange({ ...filter, values: [] })}
				>
					Clear
				</Button>
			)}
		</div>
	)
}

const Label = ({ children }: { children: React.ReactNode }) => (
	<p className="text-xs font-medium text-gray-500">{children}</p>
)

/** Send nothing rather than an empty string, which the engine would reject. */
const blankToUndefined = (value: string) => (value === '' ? undefined : value)

export default FilterChip
